import { ComboboxField } from '@monorepo/shared/componentsV2/fields/ComboboxField';
import { useCurrentUser } from '@monorepo/shared/hooks/useCurrentUser';
import { useProjectUserOptions } from '@monorepo/shared/hooks/useProjectUserOptions';
import {
  BaseFieldResponse,
  NullableString,
  SaveFieldValueRequest,
  SaveUserValue,
  UserValue,
} from 'mapistry-shared';
import React from 'react';
import styled from 'styled-components';
import { FormValues, isNullableString } from './types';
import {
  getUnexpectedTypeErrorAndLogToSentry,
  getValidateFunction,
} from './utils';

const StyledCombobox = styled(ComboboxField)`
  width: 18rem;
`;

type EditUserFieldValueProps = {
  className?: string;
  fieldValue?: UserValue;
  subText?: NullableString;
  isRequired: boolean;
  label: string;
  name: string;
  organizationId: string;
  projectId: string;
  isReadOnly?: boolean;
  isLabelHidden?: boolean;
  isErrorTextHidden?: boolean;
  isSubtextHidden?: boolean;
  isCompact?: boolean;
  forceShowErrors?: boolean;
};

export function EditUserFieldValue({
  className,
  fieldValue,
  subText,
  isRequired,
  label,
  name,
  projectId,
  organizationId,
  isReadOnly,
  isLabelHidden,
  isErrorTextHidden,
  isSubtextHidden,
  isCompact,
  forceShowErrors,
}: EditUserFieldValueProps) {
  const savedValue = fieldValue?.value?.id;

  const { userOptions = [], isLoading: projectUsersAreLoading } =
    useProjectUserOptions({
      organizationId,
      projectId,
      previouslySavedUserIds: savedValue ? [savedValue] : undefined,
    });

  const { currentUser, isLoading: currentUserIsLoading } = useCurrentUser();
  // if null was saved for the user field value, do not default to the current user
  const initialValue = fieldValue ? savedValue : currentUser?.id;

  return (
    <StyledCombobox
      autoHighlight
      className={className}
      subText={subText}
      initialValue={initialValue}
      label={label}
      isLabelHidden={isLabelHidden}
      isLoading={projectUsersAreLoading || currentUserIsLoading}
      loadingText="Loading users"
      name={name}
      options={userOptions}
      required={isRequired}
      validate={getValidateFunction(isRequired)}
      isReadOnly={isReadOnly}
      isErrorTextHidden={isErrorTextHidden}
      isSubtextHidden={isSubtextHidden}
      isCompact={isCompact}
      forceShowErrors={forceShowErrors}
    />
  );
}

export function getUserFieldValueFromForm(
  field: BaseFieldResponse,
  formValues: FormValues,
): SaveFieldValueRequest {
  const value = formValues[field.id] || null;
  if (!isNullableString(value)) {
    throw getUnexpectedTypeErrorAndLogToSentry(field, value);
  }

  const fieldValue: SaveUserValue = { value };

  return {
    fieldId: field.id,
    fieldValue,
  };
}
